import { useState, useRef, useEffect, useCallback } from "react";
import { postForm } from "../utils/api";
import "./Contact.css";
import PhoneDisplay from "./PhoneDisplay";
import EmailDisplay from "./EmailDisplay";

function Contact() {
  const [captchaCode, setCaptchaCode] = useState("");
  const [userInput, setUserInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const canvasRef = useRef(null);

  // Form state
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");

  const drawCaptcha = useCallback((code) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    canvas.width = 180;
    canvas.height = 42;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#1e293b";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.font = "bold 22px Arial";
    ctx.fillStyle = "#ffffff";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const charSpacing = 28;
    const startX = 30;

    for (let i = 0; i < code.length; i++) {
      const char = code[i];
      const x = startX + i * charSpacing;
      const y = 21;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((Math.random() - 0.5) * 0.3);
      ctx.fillText(char, 0, 0);
      ctx.restore();
    }

    ctx.strokeStyle = "#334155";
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }
  }, []);

  const generateCaptcha = useCallback(() => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let code = "";
    for (let i = 0; i < 5; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(code);
    drawCaptcha(code);
  }, [drawCaptcha]);

  useEffect(() => {
    generateCaptcha();
  }, [generateCaptcha]);

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phoneRegex = /^\d{10}$/;

    if (!name.trim()) newErrors.name = "Name is required.";
    if (!email.trim()) newErrors.email = "Email is required.";
    else if (!emailRegex.test(email.trim()))
      newErrors.email = "Enter a valid email address.";

    if (!phone.trim()) newErrors.phone = "Phone number is required.";
    else if (!phoneRegex.test(phone.trim()))
      newErrors.phone = "Phone must be exactly 10 digits.";

    if (!message.trim()) newErrors.message = "Message is required.";

    if (!userInput.trim()) newErrors.captcha = "CAPTCHA is required.";
    else if (userInput.toUpperCase() !== captchaCode)
      newErrors.captcha = "CAPTCHA code is incorrect.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    const payload = {
      name: name.trim(),
      company: company.trim(),
      email: email.trim(),
      phone: phone.trim(),
      message: message.trim(),
    };

    setIsSubmitting(true);
    try {
      const response = await postForm("/api/forms/contact", payload);
      setSuccessMsg(response?.message || "✅ Form submitted successfully!");
      setName("");
      setCompany("");
      setEmail("");
      setPhone("");
      setMessage("");
      setUserInput("");
      setErrors({});
      generateCaptcha();

      setTimeout(() => setSuccessMsg(""), 5000);
    } catch (error) {
      console.error("Contact form submission failed", error);
      setErrors({ submit: error.message || "Something went wrong. Please try again." });
      generateCaptcha();
      setUserInput("");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page-img1">
      {/* Header Label */}
      <div className="contact-page-header">
        <div className="section-label">
          <svg
            className="about-label-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16Z"
              fill="currentColor"
            />
          </svg>
          <span>Contact Us</span>
        </div>
      </div>

      {/* Main 2-Column Section Matching Image 1 Design */}
      <div className="contact-img1-container">
        {/* Left Column: Headline, Text & Contact Details */}
        <div className="contact-img1-left">
          <h1 className="contact-img1-main-title">Hi, what's up?</h1>
          <p className="contact-img1-subtitle">
            Are you interested in our offer? Or perhaps you have some questions?
            Please contact us! We'd be happy to help, answer questions, and
            develop a tailored collaboration offer!
          </p>

          <div className="contact-img1-info-block">
            <h3 className="contact-img1-info-heading">Contact</h3>

            <div className="contact-img1-info-list">
              <div className="contact-img1-info-item">
                <div className="contact-img1-icon-circle">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <span>
                  11-182, First Floor, Kurraparreddy, Vijayawada & Stamford, USA
                </span>
              </div>

              <div className="contact-img1-info-item">
                <div className="contact-img1-icon-circle">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </div>
                <PhoneDisplay phone="+91 98765 43210" />
              </div>

              <div className="contact-img1-info-item">
                <div className="contact-img1-icon-circle">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <EmailDisplay email="info@pinkmoontechnologies.com" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Organic Dark Navy Card with Contact Form (Image 1 Style) */}
        <div className="contact-img1-right-navy-card">
          {successMsg && <p className="contact-success-msg">{successMsg}</p>}
          {errors.submit && <p className="contact-error-msg">{errors.submit}</p>}

          <form className="contact-img1-form" onSubmit={handleSubmit} noValidate>
            <div className="img1-form-field">
              <label>Name and surname *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
              />
              {errors.name && <p className="contact-field-error">{errors.name}</p>}
            </div>

            <div className="img1-form-field">
              <label>Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
              />
              {errors.email && <p className="contact-field-error">{errors.email}</p>}
            </div>

            <div className="img1-form-field">
              <label>Phone number *</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setPhone(value);
                }}
                maxLength="10"
                placeholder="Enter your 10-digit phone number"
              />
              {errors.phone && <p className="contact-field-error">{errors.phone}</p>}
            </div>

            <div className="img1-form-field">
              <label>Message content *</label>
              <textarea
                rows="3"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write your message here..."
              ></textarea>
              {errors.message && <p className="contact-field-error">{errors.message}</p>}
            </div>

            {/* Captcha Area */}
            <div className="img1-captcha-wrap">
              <div className="captcha-container">
                <canvas ref={canvasRef} className="captcha-canvas"></canvas>
                <button
                  type="button"
                  className="captcha-refresh"
                  onClick={generateCaptcha}
                  aria-label="Refresh CAPTCHA"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 4V10H7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M23 20V14H17"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <input
                  type="text"
                  id="captcha"
                  name="captcha"
                  placeholder="CAPTCHA"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onPaste={(e) => e.preventDefault()}
                  autoComplete="off"
                  className="captcha-input"
                  maxLength={5}
                />
              </div>
              {errors.captcha && <p className="contact-field-error">{errors.captcha}</p>}
            </div>

            <button
              type="submit"
              className="img1-send-msg-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>

      {/* Google Map Section BELOW the form (down after the form as requested) */}
      <div className="contact-bottom-map-section">
        <div className="contact-map-card">
          <iframe
            title="PINKMOON Technologies Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3825.7541190564507!2d80.6744815751449!3d16.487983384253898!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a35fb83ef0938db%3A0xbcd43d6aba30cb0e!2sPINKMOON%20Technologies%20Private%20Limited!5e0!3m2!1sen!2sin!4v1706595783754!5m2!1sen!2sin"
            width="100%"
            height="380"
            style={{ border: 0, borderRadius: "16px" }}
            allowFullScreen
            loading="lazy"
          ></iframe>
        </div>
      </div>
    </div>
  );
}

export default Contact;
